import * as React from 'react';

import { Binding } from 'core/infrastructure';
import { cloneExcept, Currency, Environment } from 'core/utils';
import { FormComponent } from './Form';
import { Constraint, positive, maxDecimalPlaces } from './Constraints';
import * as Format from './Format';
import { TextBox, TextBoxCtor } from './TextBox';

interface Props<T> extends React.DetailedHTMLProps<React.InputHTMLAttributes<DecimalBox<T>>, DecimalBox<T>> {
	linkTo: Binding.PropertyPath<T>;
	precision: number;
	label?: string | React.ReactNode;
	rules?: Constraint | Constraint[];
	format?: (value: any) => string;
	onEnterPress?: () => void;
	onValueChange?: (value: string) => void;
	boundValue?: any;
	allowNegative?: boolean;
}

export class DecimalBox<T> extends React.Component<Props<T>, EmptyState> implements FormComponent {

	constructor(props: Props<T>) {
		super(props);
	}

	private textBox = React.createRef<TextBox<T>>();

	getPath() {
		return this.textBox.current!.getPath();
	}

	showErrors(errors: string[]) {
		this.textBox.current!.showErrors(errors);
	}

	getValue() {
		return this.textBox.current!.getValue();
	}

	setValue(value: number) {
		this.textBox.current!.setValue(value);
	}

	activate() {
		this.textBox.current!.activate();
	}

	render() {
		const TheTextBox: TextBoxCtor<T> = TextBox;

		const props = cloneExcept(this.props, ['ref', 'allowNegative', 'precision']);

		props.rules = !props.rules ? [] : typeof props.rules === 'function' ? [props.rules] : props.rules;

		props.rules.push(maxDecimalPlaces(this.props.precision));
		if (!this.props.allowNegative) {
			props.rules.push(positive);
		}

		props.propertyType = 'decimal';
		if (!this.props.allowNegative) {
			props.min = 0;
		}

		props.onInput = (evt: React.KeyboardEvent<HTMLInputElement>) => {

			const start = evt.currentTarget.selectionStart;
			const end = evt.currentTarget.selectionEnd;

			const replaced = evt.currentTarget.value.replace('.', Currency.DECIMAL_SEPARATOR);
			if (replaced !== evt.currentTarget.value) {
				evt.currentTarget.value = replaced;
				evt.currentTarget.selectionStart = start;
				evt.currentTarget.selectionEnd = end;
			}
		};

		// Dejo text y gestiono a mano lo que se puede poner y lo que no
		// para poder usar el . del teclado numérico en navegadores como Chrome
		props.type = 'text';

		props.format = props.format || Format.asDecimalWith(this.props.precision, false);
		props.autoComplete = 'off';

		// En Safari sobre iOS se producen fallos a la hora
		// de escribir en inputs formateados con decimales.
		// Para evitar estos fallos se usa este hack sacado de
		// https://github.com/reactjs/react-modal/issues/233#issuecomment-250532918
		if (Environment.isiOS())
			props.style = { transform: 'translate3d(0px, 0px, 0px)' };

		return <TheTextBox ref={this.textBox} {...props} />;
	}
}

export type DecimalBoxCtor<T> = new (props: Props<T>) => DecimalBox<T>;
